<template>
    <ListLayout form-title="Add Inquiry" :show-form="isAddItemRequested" :has-list-scroll="true">
        <template #list-form>
            <InquiryForm
                v-if="isAddItemRequested && !isApplicationHomeCare && !isApplicationHomeHealth"
                :inquiry="newInquiry"
                :isLoading="isSaveNewInProgress"
                :serviceTypeEnums="serviceTypeEnums"
                :branches="currentAddBranches"
                v-on:updateBranch="updateAddBranch"
                ref="inquiryForm"
            />
            <InquiryFormHomecareHomehealth
                v-if="isAddItemRequested && (isApplicationHomeCare || isApplicationHomeHealth)"
                :inquiry="newInquiry"
                :isLoading="isSaveNewInProgress"
                :branches="currentAddBranches"
                v-on:updateBranch="updateAddBranch"
                ref="homecareInquiryForm"
            />
            <div class="text-center pt-2">
                <button type="button" class="btn btn-primary btn-md" @click="saveNewInquiry()">Add Inquiry</button>
                <button
                    type="button"
                    class="btn btn-outline-secondary btn-md ms-2"
                    @click="closeAddInquiry(hospiceApplicationId)"
                >
                    Cancel
                </button>
            </div>
        </template>
        <template #list-header>
            <div class="input-group search-container">
                <input
                    :disabled="isLoading"
                    type="text"
                    class="form-control border-end-0"
                    placeholder="Search Inquiries by Patient Name"
                    v-model="term"
                />
                <span class="input-group-append">
                    <div class="input-group-text bg-transparent p-2" @click="search()">
                        <i class="fas fa-search font-size-sm"></i>
                    </div>
                </span>
            </div>
        </template>
        <template #list-header-drop>
            <div class="me-0 ms-lg-4">
                <MultiSelect
                    :drop-down-options="agencyMultiSelectOptions"
                    :selected-options="selectedAgencies"
                    v-on:updateBranches="updateBranchesbyAgency"
                    label-text="Organizations"
                />
            </div>
            <div class="me-auto ms-lg-4">
                <BranchDefaultFilter
                    :check-all-by-default="true"
                    :selected-branches="selectedBranches"
                    v-on:updateBranches="updateBranches"
                    :selected-agencies-name="selectedAgenciesName"
                    :is-inquiry="true"
                />
            </div>
        </template>
        <template #list-header-new>
            <div class="btn-group">
                <button
                    v-if="excelExportPermission"
                    type="button"
                    class="btn btn-sm btn-primary ms-lg-2"
                    @click="downloadChart()"
                >
                    Export to Excel
                </button>
            </div>
            <div
                class="btn-group"
                v-if="
                    !isAddItemRequested &&
                    (canAddHospiceInquiry ||
                        canAddPalliativeInquiry ||
                        canAddHomeCareInquiry ||
                        canAddHomeHealthInquiry)
                "
            >
                <button
                    type="button"
                    class="btn btn-sm btn-primary ms-lg-2 dropdown-toggle"
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    aria-controls="newInquiryDropdown"
                >
                    New
                </button>
                <div class="dropdown-menu" id="newInquiryDropdown">
                    <a v-if="canAddHospiceInquiry" class="dropdown-item" @click="openAddInquiry(hospiceApplicationId)"
                        >Axxess Hospice</a
                    >
                    <a
                        v-if="canAddPalliativeInquiry"
                        class="dropdown-item"
                        @click="openAddInquiry(palliativeApplicationId)"
                        >Axxess Palliative Care</a
                    >
                    <a v-if="canAddHomeCareInquiry" class="dropdown-item" @click="openAddInquiry(homeCareApplicationId)"
                        >Axxess Home Care</a
                    >
                    <a
                        v-if="canAddHomeHealthInquiry"
                        class="dropdown-item"
                        @click="openAddInquiry(homeHealthApplicationId)"
                        >Axxess Home Health</a
                    >
                </div>
            </div>
        </template>
        <div class="h-100 d-flex flex-column mb-3 mt-2">
            <Pagination
                ref="paginationInstance"
                :pageChanged="pageChangedCallback"
                :isLoading="isLoading"
                entity="Inquiries"
                :showPageSizeOptions="true"
                classes="pagination-footer"
                :pageSize="pageSize"
                v-on:updatePageSizeChanged="updatePageSizeChanged"
            >
                <StaticTableFormater
                    :enabled="true"
                    :isLoading="!(!isLoading && hasData)"
                    :backColumnCount="1"
                    :frontColumnCount="4"
                    :tableColumnSettings="getCustomSettings()"
                    :columnsAltered="dynamicTableToggled"
                    v-model:columns="columns"
                >
                    <table class="table table-responsive-md border border-gray">
                        <thead>
                            <tr class="bg-table-header table-header">
                                <th v-if="hasData">
                                    <SortableTh
                                        :sort-by="sortBy"
                                        v-on:sortByUpdated="sortByUpdated"
                                        :sort-order="sortOrder"
                                        v-on:sortOrderUpdated="sortOrderUpdated"
                                        sort-string="date"
                                    >
                                        Inquiry Date</SortableTh
                                    >
                                </th>
                                <th v-else>Inquiry/Lead Date</th>
                                <th v-if="hasData">
                                    <SortableTh
                                        :sort-by="sortBy"
                                        v-on:sortByUpdated="sortByUpdated"
                                        :sort-order="sortOrder"
                                        v-on:sortOrderUpdated="sortOrderUpdated"
                                        sort-string="lastName"
                                    >
                                        Patient Information<i
                                            class="fas fa-question-circle ms-1"
                                            v-tooltip="{ title: `Sort by Patient Name` }"
                                        ></i>
                                    </SortableTh>
                                </th>
                                <th v-else>Patient Information</th>
                                <th v-if="hasData">
                                    <SortableTh
                                        :sort-by="sortBy"
                                        v-on:sortByUpdated="sortByUpdated"
                                        :sort-order="sortOrder"
                                        v-on:sortOrderUpdated="sortOrderUpdated"
                                        sort-string="contactLastName"
                                    >
                                        Contact Information<i
                                            class="fas fa-question-circle ms-1"
                                            v-tooltip="{ title: `Sort by Contact Name` }"
                                        ></i
                                    ></SortableTh>
                                </th>
                                <th v-else>Contact Information</th>
                                <th v-if="hasData">
                                    <SortableTh
                                        :sort-by="sortBy"
                                        v-on:sortByUpdated="sortByUpdated"
                                        :sort-order="sortOrder"
                                        v-on:sortOrderUpdated="sortOrderUpdated"
                                        sort-string="branchName"
                                    >
                                        Branch
                                    </SortableTh>
                                </th>
                                <th v-else>Branch</th>
                                <th v-if="hasData">
                                    <SortableTh
                                        :sort-by="sortBy"
                                        v-on:sortByUpdated="sortByUpdated"
                                        :sort-order="sortOrder"
                                        v-on:sortOrderUpdated="sortOrderUpdated"
                                        sort-string="servicesRequested"
                                    >
                                        Service Requested
                                    </SortableTh>
                                </th>
                                <th v-else>Service Requested</th>
                                <th v-if="hasData">
                                    <SortableTh
                                        :sort-by="sortBy"
                                        v-on:sortByUpdated="sortByUpdated"
                                        :sort-order="sortOrder"
                                        v-on:sortOrderUpdated="sortOrderUpdated"
                                        sort-string="assignedUser"
                                    >
                                        Assigned Team Member
                                    </SortableTh>
                                </th>
                                <th v-else>Assigned Team Member</th>
                                <th v-if="hasData">
                                    <SortableTh
                                        :sort-by="sortBy"
                                        v-on:sortByUpdated="sortByUpdated"
                                        :sort-order="sortOrder"
                                        v-on:sortOrderUpdated="sortOrderUpdated"
                                        sort-string="followUpDate"
                                    >
                                        Follow-Up Date
                                    </SortableTh>
                                </th>
                                <th v-else>Follow-Up Date</th>
                                <th class="text-center">Comment</th>
                                <th class="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- EMPTY -->
                            <tr
                                class="table-empty"
                                v-if="!isLoading && !isError && !hasData && !branchRef?.isRequestPending"
                            >
                                <td :colspan="columns">
                                    <h3>No Inquiries/Leads Found.</h3>
                                </td>
                            </tr>
                            <!-- ERROR -->

                            <tr class="table-error" v-if="!isLoading && isError && !branchRef?.isRequestPending">
                                <td :colspan="columns">
                                    <ErrorMessageHandler title="Inquiry list" :show-try-again="true" :try-again="reset">
                                    </ErrorMessageHandler>
                                </td>
                            </tr>

                            <!-- LOADING -->
                            <template v-if="isLoading || branchRef?.isRequestPending">
                                <tr v-for="i in loadingCount" class="table-loading" :key="`count-${i}`">
                                    <td v-for="i in columns" :key="`column-${i}`">
                                        <span class="shimmer-loading"></span>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                        <!-- LIST -->
                        <template v-if="!isLoading && !isError && hasData && !branchRef?.isRequestPending">
                            <tbody v-for="inquiry in page.items" :key="inquiry.id">
                                <CentralInquiryRow
                                    :lineOfServiceEnumMap="lineOfServiceEnumMap"
                                    :inquiry="inquiry"
                                    :columns="columns"
                                    :resetList="reset"
                                    :referralsAddable="referralsAddable"
                                    :serviceTypeEnums="serviceTypeEnums"
                                    :branches="currentEditBranches"
                                    v-model:isEditSaveInProgress="isEditSaveInProgress"
                                    v-on:updateBranch="updateEditBranch"
                                />
                            </tbody>
                        </template>
                    </table>
                </StaticTableFormater>
            </Pagination>
        </div>
    </ListLayout>
</template>

<script lang="ts" src="./CentralInquiry.ts"></script>
